export const NCRM_LEAD_BOARD_FILTER_STORAGE_KEY = "ncrm-lead-board-filters";

const FILTER_LOGICS = Object.freeze(["and", "or"]);
const FILTER_OPERATORS = Object.freeze(["equals", "contains"]);

const normalizeText = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "";
};

const normalizeBoardId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return String(Math.trunc(parsed));
};

const normalizeLogic = (value, fallback = "and") => {
  const normalized = normalizeText(value).toLowerCase();
  if (FILTER_LOGICS.includes(normalized)) return normalized;
  return FILTER_LOGICS.includes(fallback) ? fallback : "and";
};

const normalizeOperator = (value, fallback = "equals") => {
  const normalized = normalizeText(value).toLowerCase();
  if (FILTER_OPERATORS.includes(normalized)) return normalized;
  return FILTER_OPERATORS.includes(fallback) ? fallback : "equals";
};

const normalizeRule = (rule = {}) => {
  const fieldKey = normalizeText(rule.field_key ?? rule.fieldKey).toLowerCase();
  const value = normalizeText(rule.value ?? rule.term);
  if (!fieldKey || !value) return null;
  return {
    field_key: fieldKey,
    field_label: normalizeText(rule.field_label ?? rule.fieldLabel),
    field_group: normalizeText(rule.field_group ?? rule.fieldGroup).toLowerCase(),
    operator: normalizeOperator(rule.operator),
    value,
  };
};

const normalizeRules = (value) => {
  const source = Array.isArray(value) ? value : [];
  return source.map(normalizeRule).filter(Boolean);
};

const readStorageSnapshot = (storage) => {
  if (!storage || typeof storage.getItem !== "function") return {};
  try {
    const raw = storage.getItem(NCRM_LEAD_BOARD_FILTER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeStorageSnapshot = (storage, snapshot) => {
  if (!storage || typeof storage.setItem !== "function") return;
  try {
    storage.setItem(NCRM_LEAD_BOARD_FILTER_STORAGE_KEY, JSON.stringify(snapshot));
  } catch (_) {
    /* ignore storage issues */
  }
};

export const normalizeLeadBoardFilterConfig = (value = {}) => {
  const rules = normalizeRules(value.rules ?? value.items ?? value.filters);
  return {
    logic: normalizeLogic(value.logic ?? value.filters_logic ?? value.filtersLogic, "and"),
    rules,
  };
};

export const hasLeadBoardFilters = (value = {}) => normalizeLeadBoardFilterConfig(value).rules.length > 0;

export const loadLeadBoardFilters = ({
  boardId,
  storage = globalThis?.localStorage,
} = {}) => {
  const normalizedBoardId = normalizeBoardId(boardId);
  if (!normalizedBoardId) return normalizeLeadBoardFilterConfig();
  const snapshot = readStorageSnapshot(storage);
  return normalizeLeadBoardFilterConfig(snapshot?.[normalizedBoardId]);
};

export const saveLeadBoardFilters = ({
  boardId,
  filters,
  storage = globalThis?.localStorage,
} = {}) => {
  const normalizedBoardId = normalizeBoardId(boardId);
  if (!normalizedBoardId) return normalizeLeadBoardFilterConfig();
  const snapshot = readStorageSnapshot(storage);
  const normalizedFilters = normalizeLeadBoardFilterConfig(filters);
  if (normalizedFilters.rules.length) {
    snapshot[normalizedBoardId] = normalizedFilters;
    writeStorageSnapshot(storage, snapshot);
    return normalizedFilters;
  }
  delete snapshot[normalizedBoardId];
  writeStorageSnapshot(storage, snapshot);
  return normalizedFilters;
};

export const clearLeadBoardFilters = ({
  boardId,
  storage = globalThis?.localStorage,
} = {}) => {
  const normalizedBoardId = normalizeBoardId(boardId);
  if (!normalizedBoardId) return;
  const snapshot = readStorageSnapshot(storage);
  if (!Object.prototype.hasOwnProperty.call(snapshot, normalizedBoardId)) return;
  delete snapshot[normalizedBoardId];
  writeStorageSnapshot(storage, snapshot);
};

export const clearLeadBoardFilterStorage = (storage = globalThis?.localStorage) => {
  if (!storage || typeof storage.removeItem !== "function") return;
  try {
    storage.removeItem(NCRM_LEAD_BOARD_FILTER_STORAGE_KEY);
  } catch (_) {
    /* ignore storage issues */
  }
};

export const serializeLeadBoardFilters = (value = {}) => {
  const normalized = normalizeLeadBoardFilterConfig(value);
  return normalized.rules.length ? JSON.stringify(normalized) : "";
};

export const LEAD_BOARD_FILTER_LOGIC_VALUES = FILTER_LOGICS;
export const LEAD_BOARD_FILTER_OPERATOR_VALUES = FILTER_OPERATORS;
